import { get, intersection } from 'lodash';

export function utilGet(data, pathOrFunc, ...additionalArgs) {
  if (typeof pathOrFunc === 'function') {
    // Call the function with the row and any additional arguments
    return pathOrFunc(data, ...additionalArgs);
  }

  // Otherwise, use lodash's get to handle string and array paths or full data if emptystring
  return pathOrFunc !== undefined && pathOrFunc === '' ? data : get(data, pathOrFunc);
}

// Enhanced get function with default value handling
export function utilGetWithDefault(data, pathOrFunc, defaultValue, ...additionalArgs) {
  const result = utilGet(data, pathOrFunc, ...additionalArgs);
  // Return the result if it's truthy, or the default value if the result is falsy
  return result || defaultValue;
}

// ── WHAT A COLUMN SORTS ON IS NOT ALWAYS WHAT IT DISPLAYS ──────────────────
//
// A column def's `rowCellSource` is a DISPLAY accessor, and very often a formatter — a
// moment format, an `intl.formatMessage`, sometimes a whole React element. Ordering on its
// output compares the rendered text, so `DD.MM.YYYY` sorts by day-of-month, a non-padded
// `H:mm` puts "7:05" above "10:05", a localised label re-orders itself when the locale
// changes, and an element compares false against everything so rows never move while the
// header still draws an arrow.
//
// `sortSource` is the escape hatch. It takes exactly the same string-or-function shape
// `rowCellSource` takes, because `utilGet` above already dispatches on `typeof` — so a
// column names the raw value it MEANS to order by, usually the same path its table's own
// `defaultSort` already uses. Omit it and nothing changes.
//
// Only an ABSENT property defers to `rowCellSource`: '' is a meaningful `utilGet` path (it
// means "the whole row"), so a falsy-but-present `sortSource` is honoured as given.
//
// Every "which column is this sort for?" comparison must come through here — `handleSort`,
// `getSortIndicator` and `getSortedData` all ask it, and they have to agree or the sort
// arrow ends up over a different column than the one being sorted.
export function sortKeyOf(colDef) {
  return colDef.sortSource !== undefined ? colDef.sortSource : colDef.rowCellSource;
}

// Which single column carries the sort arrow.
//
// `sortColumns[].source` says what to ORDER BY, and that is NOT a column identity: two
// columns may legitimately order by the same field — ECA history renders a date and a time
// that are both formats of `startTime`, so both carry `sortSource: 'startTime'`. Matching the
// indicator on the source alone drew an arrow on BOTH.
//
// `colKey` (a column's `headerIntlKey`) names the clicked column and wins when present.
// `defaultSort` is authored in the table def with no column reference at all, so on first
// render the arrow falls to the FIRST column whose sort key matches — deterministic, and the
// alternative is two arrows.
export function sortedColumnKey(columns, primarySort) {
  if (!primarySort) return null;
  if (primarySort.colKey !== undefined) return primarySort.colKey;
  const first = (columns || []).find(col => sortKeyOf(col) === primarySort.source);
  return first ? first.headerIntlKey : null;
}

export function allowedRoles(actionDef, userRoles) {
  // If allowedRoles is not defined in actionDef, return true (allow by default)
  if (!(actionDef && actionDef.allowedRoles)) {
    return true;
  }

  // Compute the intersection of user roles and allowed roles
  const commonRoles = intersection(userRoles, actionDef.allowedRoles);

  // Return true if there is any overlap, false otherwise
  return commonRoles.length > 0;
}

export function rowsFromSelectedIds(dataRows, selectedIds) {
  const dataDict = dataRows.reduce((acc, row) => {
    acc[row.id] = row;
    return acc;
  }, {});
  return selectedIds.map(rowId => dataDict[rowId]);
}
